// Moteur MLS des dossiers : synchronisation, envoi, invitation.
//
// Tout se joue autour d'une contrainte de MLS, qu'il faut avoir en tête pour
// lire ce fichier :
//
//   • **un message ne se déchiffre qu'une seule fois** (le ratchet avance) ;
//   • **on ne peut pas déchiffrer ses propres messages** (même feuille) ;
//   • les messages doivent être traités **dans l'ordre**.
//
// D'où le modèle : chaque appareil garde, dans une enveloppe chiffrée unique
// (cf. crypto/mls-state.ts), l'état MLS du groupe, la liste des messages déjà
// appliqués au ratchet, et les clairs déjà déchiffrés. Synchroniser = appliquer
// au ratchet, dans l'ordre, les seuls messages encore inconnus.

import { api } from '../api/client';
import {
  groupOf,
  listDevices,
  loadDossierState,
  saveClientState,
  saveDossierState,
  type CachedMessage,
  type DossierState,
} from '../crypto/mls-state';
import {
  MlsClient,
  MlsGroup,
  decryptBlob,
  encryptBlob,
  loadCrypto,
} from '../crypto';

const enc = new TextEncoder();
const dec = new TextDecoder();

const toBase64 = (b: Uint8Array) => btoa(String.fromCharCode(...b));
const fromBase64 = (s: string) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0));

/**
 * Les messages `handshake` transportent deux choses très différentes : le
 * *commit* (pour les membres déjà là) et le *Welcome* (pour l'arrivant). Le
 * serveur ne connaît qu'un seul type « handshake » — on les distingue donc par
 * un octet de tête, qui lui reste parfaitement opaque.
 */
const COMMIT = 0x01;
const WELCOME = 0x02;

const tagged = (tag: number, payload: Uint8Array) => {
  const out = new Uint8Array(payload.length + 1);
  out[0] = tag;
  out.set(payload, 1);
  return out;
};

/**
 * Contenu d'un message applicatif.
 *
 * Le canal MLS ne sert pas qu'à transporter du texte : c'est aussi le seul
 * moyen de confier un secret à **tous les membres** d'un dossier sans que le
 * serveur l'apprenne. On y fait donc passer la clé du dossier, d'où cette
 * enveloppe typée.
 */
type Envelope =
  | { k: 'text'; v: string }
  | { k: 'dossier-key'; v: string }; // base64

const encodeEnvelope = (e: Envelope) => enc.encode(JSON.stringify(e));

function decodeEnvelope(bytes: Uint8Array): Envelope | null {
  try {
    const parsed: unknown = JSON.parse(dec.decode(bytes));
    if (
      parsed &&
      typeof parsed === 'object' &&
      'k' in parsed &&
      'v' in parsed &&
      typeof (parsed as Envelope).v === 'string'
    ) {
      return parsed as Envelope;
    }
  } catch {
    // Message d'une version antérieure du format, ou corrompu : on l'ignore
    // plutôt que de faire échouer toute la synchronisation.
  }
  return null;
}

// ---------------------------------------------------------------------------
// Types de l'API
// ---------------------------------------------------------------------------

/**
 * Tout ce qui décrit un dossier — **et tout est chiffré**.
 *
 * L'intitulé (« Divorce X c/ Y »), bien sûr. Mais aussi la **référence** : dans un
 * vrai cabinet elle s'écrit « DUPONT-2026-04 », elle porte le nom du client. Et le
 * **type d'affaire** : `penal`, `divorce`, recoupés avec les participants que le
 * serveur connaît, ils livrent « ce client a une affaire pénale avec cet avocat ».
 *
 * Les trois vivaient autrefois en clair côté serveur. Rien ne le justifiait : il
 * trie sur `openedAt`, jamais sur eux.
 */
export type DossierDetails = {
  name: string;
  reference: string;
  affaireType: string;
  summary?: string;
};

export type Dossier = {
  id: string;
  /**
   * Détails **déchiffrés**. `null` tant qu'on n'a pas la clé du dossier — on vient
   * d'y être invité, et elle arrivera par le groupe MLS à la synchronisation.
   */
  details: DossierDetails | null;
  /** Le blob, gardé pour le déchiffrer dès que la clé arrive. */
  encryptedDetails: string;
  status: string;
  mlsGroupId: string;
  openedAt: string;
};

type RawDossier = Omit<Dossier, 'details'>;

/** AAD des détails : lie le blob à son usage. */
const DETAILS_AAD = enc.encode('delo/dossier-details/v1');

/**
 * Déchiffre les détails d'un dossier, si l'on en a la clé.
 *
 * `null` quand on ne l'a pas encore reçue. On n'a alors **rien** à afficher qu'un
 * dossier en attente — et c'est précisément ce que voit le serveur, lui, pour
 * toujours.
 */
export function decryptDossierDetails(
  dossierKey: Uint8Array | null,
  encryptedDetails: string,
): DossierDetails | null {
  if (!dossierKey) return null;
  try {
    return JSON.parse(
      dec.decode(decryptBlob(dossierKey, fromBase64(encryptedDetails), DETAILS_AAD)),
    ) as DossierDetails;
  } catch {
    return null;
  }
}

type RelayedMessage = {
  id: string;
  authorId: string;
  encryptedPayload: string;
  messageKind: 'application' | 'handshake' | string;
  serverTimestamp: string;
};

/** Contexte d'appel : qui je suis, avec quelles clés. */
export type Ctx = {
  token: string;
  masterKey: Uint8Array;
  deviceId: string;
  userId: string;
  client: MlsClient;
  /** Seul l'appareil principal ajoute les appareils secondaires aux groupes. */
  isPrimary: boolean;
};

// ---------------------------------------------------------------------------
// KeyPackages — sans eux, personne ne peut être invité
// ---------------------------------------------------------------------------

const KP_TARGET = 5;

/**
 * Maintient un stock de KeyPackages publiés pour cet appareil.
 *
 * Un KeyPackage est à usage unique : sans stock, personne ne pourra nous
 * inviter dans un dossier. On se réapprovisionne donc à chaque connexion.
 */
export async function topUpKeyPackages(ctx: Ctx): Promise<number> {
  await loadCrypto();
  const { available } = await api<{ available: number }>('/key-packages', {
    token: ctx.token,
  });
  if (available >= KP_TARGET) return available;

  const fresh: string[] = [];
  for (let i = available; i < KP_TARGET; i++) {
    fresh.push(toBase64(ctx.client.getKeyPackage()));
  }

  const { available: now } = await api<{ available: number }>('/key-packages', {
    method: 'POST',
    token: ctx.token,
    body: { keyPackages: fresh },
  });

  // `getKeyPackage` a écrit les clés privées correspondantes dans le keystore :
  // sans re-déposer l'état client, elles seraient perdues à la reconnexion et
  // les KeyPackages publiés deviendraient inutilisables.
  await saveClientState(ctx.token, ctx.masterKey, ctx.deviceId, ctx.client);
  return now;
}

// ---------------------------------------------------------------------------
// Dossiers
// ---------------------------------------------------------------------------

/**
 * Liste les dossiers **et déchiffre leur intitulé**.
 *
 * L'intitulé est chiffré avec la clé du dossier, qui vit dans l'état chiffré de
 * cet appareil : il faut donc rapatrier cet état, dossier par dossier. C'est une
 * requête par dossier — acceptable à cette échelle, mais c'est le premier
 * endroit à revoir si un cabinet en accumule beaucoup.
 */
export async function listDossiers(ctx: Ctx): Promise<Dossier[]> {
  await loadCrypto();
  const { dossiers } = await api<{ dossiers: RawDossier[] }>('/dossiers', {
    token: ctx.token,
  });

  return Promise.all(
    dossiers.map(async (d) => {
      let details: DossierDetails | null = null;
      try {
        const stored = await loadDossierState(
          ctx.token,
          ctx.masterKey,
          ctx.deviceId,
          d.id,
        );
        details = decryptDossierDetails(
          stored?.state.dossierKey ?? null,
          d.encryptedDetails,
        );
      } catch {
        // Pas encore d'état pour ce dossier (on vient d'y être invité) : la clé
        // arrivera par le groupe MLS à la synchronisation, et les détails avec.
      }
      return { ...d, details };
    }),
  );
}

/** Crée un dossier : le groupe MLS naît ici, dans le navigateur. */
export async function createDossier(
  ctx: Ctx,
  details: DossierDetails,
): Promise<Dossier> {
  await loadCrypto();

  const groupId = crypto.getRandomValues(new Uint8Array(32));
  const group = ctx.client.createGroup(groupId);

  // Le secret commun du dossier naît ici, dans le navigateur. Il sera confié aux
  // futurs membres par le groupe MLS, jamais par le serveur — et c'est lui qui
  // chiffre les détails, pour qu'ils soient lisibles par tous les membres, et par
  // eux seuls.
  const dossierKey = crypto.getRandomValues(new Uint8Array(32));

  const created = await api<RawDossier>('/dossiers', {
    method: 'POST',
    token: ctx.token,
    body: {
      mlsGroupId: toBase64(groupId),
      encryptedDetails: toBase64(
        encryptBlob(dossierKey, enc.encode(JSON.stringify(details)), DETAILS_AAD),
      ),
    },
  });

  // `createGroup` a consommé du matériel du keystore : l'état client a changé.
  await saveClientState(ctx.token, ctx.masterKey, ctx.deviceId, ctx.client);

  const state: DossierState = {
    mls: group.toState(),
    processed: [],
    messages: [],
    dossierKey,
  };
  await saveDossierState(
    ctx.token,
    ctx.masterKey,
    ctx.deviceId,
    created.id,
    state,
    group.epoch(),
  );
  return { ...created, details };
}

// ---------------------------------------------------------------------------
// Synchronisation
// ---------------------------------------------------------------------------

/** Récupère tout le flux du dossier, dans l'ordre. */
async function fetchMessages(ctx: Ctx, dossierId: string): Promise<RelayedMessage[]> {
  const all: RelayedMessage[] = [];
  let cursor: string | null = null;

  do {
    const page: { messages: RelayedMessage[]; nextCursor: string | null } =
      await api(
        `/dossiers/${dossierId}/messages?limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`,
        { token: ctx.token },
      );
    all.push(...page.messages);
    cursor = page.nextCursor;
  } while (cursor);

  return all;
}

export type SyncResult = {
  state: DossierState;
  revision: number;
  /** `false` si cet appareil n'est pas (encore) membre du groupe MLS. */
  joined: boolean;
};

/**
 * Met le dossier à jour : applique au ratchet, dans l'ordre, les messages encore
 * inconnus de cet appareil.
 *
 * Deux entrées possibles :
 *  - on a déjà un état → on applique la suite ;
 *  - on n'en a pas (on vient d'être invité) → on cherche notre Welcome dans le
 *    flux. Tout ce qui le précède est marqué traité sans être lu : la
 *    confidentialité persistante de MLS veut qu'un arrivant ne puisse pas lire
 *    l'historique d'avant son arrivée. Ce n'est pas une limite, c'est le contrat.
 */
export async function syncDossier(ctx: Ctx, dossierId: string): Promise<SyncResult> {
  await loadCrypto();

  const stored = await loadDossierState(ctx.token, ctx.masterKey, ctx.deviceId, dossierId);
  const messages = await fetchMessages(ctx, dossierId);

  let state: DossierState =
    stored?.state ?? {
      mls: new Uint8Array(),
      processed: [],
      messages: [],
      dossierKey: null,
    };
  let revision = stored?.revision;
  let group: MlsGroup | null = stored ? groupOf(state) : null;

  const processed = new Set(state.processed);
  let dirty = false;

  for (const m of messages) {
    if (processed.has(m.id)) continue;
    const payload = fromBase64(m.encryptedPayload);

    if (m.messageKind === 'handshake') {
      const tag = payload[0];
      const body = payload.subarray(1);

      if (!group && tag === WELCOME) {
        // Est-ce NOTRE Welcome ? Le seul moyen de le savoir est d'essayer :
        // celui d'un autre appareil échouera proprement.
        try {
          group = ctx.client.joinGroup(body);
          // Rejoindre consomme le KeyPackage : l'état client a changé.
          await saveClientState(ctx.token, ctx.masterKey, ctx.deviceId, ctx.client);
          dirty = true;
        } catch {
          // Welcome destiné à quelqu'un d'autre : on l'ignore.
        }
      } else if (group && tag === COMMIT) {
        group.processCommit(body);
        dirty = true;
      }
      // Un Welcome quand on est déjà membre, ou un commit avant d'avoir rejoint,
      // ne nous concerne pas : on le marque traité et on passe.
      processed.add(m.id);
      continue;
    }

    // Message applicatif.
    if (!group) {
      // Antérieur à notre arrivée : illisible par construction (forward secrecy).
      processed.add(m.id);
      continue;
    }

    try {
      const envelope = decodeEnvelope(group.decryptApplication(payload));

      if (envelope?.k === 'dossier-key') {
        // Le secret commun du dossier vient de nous être confié, par le groupe
        // lui-même. Le serveur, lui, n'a vu qu'un blob chiffré passer.
        state.dossierKey = fromBase64(envelope.v);
      } else if (envelope?.k === 'text') {
        state.messages.push({
          id: m.id,
          authorId: m.authorId,
          text: envelope.v,
          at: m.serverTimestamp,
        });
      }
    } catch (e) {
      // MLS interdit de déchiffrer ses propres messages. Si c'est le nôtre et
      // qu'on n'en a pas gardé l'écho local (envoyé depuis un autre appareil,
      // ou cache perdu), on l'affiche comme indisponible plutôt que d'échouer.
      if (m.authorId !== ctx.userId) throw e;
      state.messages.push({
        id: m.id,
        authorId: m.authorId,
        text: null,
        at: m.serverTimestamp,
      });
    }
    processed.add(m.id);
    dirty = true;
  }

  if (!group) {
    return { state, revision: revision ?? 0, joined: false };
  }

  state = { ...state, mls: group.toState(), processed: [...processed] };

  if (dirty || !stored) {
    revision = await saveDossierState(
      ctx.token,
      ctx.masterKey,
      ctx.deviceId,
      dossierId,
      state,
      group.epoch(),
      revision,
    );
  }

  return { state, revision: revision ?? 0, joined: true };
}

// ---------------------------------------------------------------------------
// Envoi
// ---------------------------------------------------------------------------

export async function sendMessage(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
  revision: number,
  text: string,
): Promise<SyncResult> {
  await loadCrypto();
  const group = groupOf(state);

  const encryptedPayload = toBase64(
    group.encryptApplication(encodeEnvelope({ k: 'text', v: text })),
  );
  const sent = await api<{ id: string; serverTimestamp: string }>(
    `/dossiers/${dossierId}/messages`,
    {
      method: 'POST',
      token: ctx.token,
      body: {
        encryptedPayload,
        messageKind: 'application',
        epochAtSend: group.epoch().toString(),
      },
    },
  );

  // On garde le clair : MLS ne nous laissera jamais relire notre propre message.
  const next: DossierState = {
    ...state,
    mls: group.toState(),
    processed: [...state.processed, sent.id],
    messages: [
      ...state.messages,
      { id: sent.id, authorId: ctx.userId, text, at: sent.serverTimestamp },
    ],
  };

  const newRevision = await saveDossierState(
    ctx.token,
    ctx.masterKey,
    ctx.deviceId,
    dossierId,
    next,
    group.epoch(),
    revision,
  );
  return { state: next, revision: newRevision, joined: true };
}

// ---------------------------------------------------------------------------
// Invitation
// ---------------------------------------------------------------------------

export type Invitee = { id: string; email: string; userType: string };

export function lookupUser(ctx: Ctx, email: string): Promise<Invitee> {
  return api<Invitee>(`/users/lookup?email=${encodeURIComponent(email)}`, {
    token: ctx.token,
  });
}

export type Participant = {
  id: string;
  userId: string;
  email: string;
  userType: string;
  role: string;
  permissions: string[];
  validFrom: string;
  validUntil: string | null;
};

/** Les membres du dossier — rôles et droits, tels que le serveur les connaît. */
export function listParticipants(
  ctx: Ctx,
  dossierId: string,
): Promise<Participant[]> {
  return api<Participant[]>(`/dossiers/${dossierId}/participations`, {
    token: ctx.token,
  });
}

/**
 * Le rôle dans le dossier découle du type de compte. Avant, tout non-avocat
 * devenait « client » — un confrère collaborateur ou un tiers (expert,
 * huissier) était donc mal étiqueté.
 */
export function roleForUserType(userType: string): string {
  if (userType === 'avocat_collaborateur') return 'collaborateur';
  if (userType.startsWith('avocat')) return 'avocat';
  if (userType === 'tiers') return 'tiers';
  return 'client';
}

/**
 * Invite quelqu'un dans le dossier : droit d'accès **et** appartenance au groupe
 * MLS. Les deux sont nécessaires — le premier ouvre la porte du serveur, le
 * second seul permet de lire quoi que ce soit.
 *
 * Un ajout MLS se fait par *feuille*, donc un par appareil de l'invité. Chaque
 * ajout produit un commit (pour les membres présents) et un Welcome (pour
 * l'arrivant) : on publie les deux dans le flux, ce qui permet à chacun de se
 * mettre à jour à son rythme, sans être en ligne au même moment.
 */
export async function inviteParticipant(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
  revision: number,
  invitee: Invitee,
  role: string,
  permissions: string[],
): Promise<SyncResult> {
  await loadCrypto();

  // 1. Le droit d'accès côté serveur (sans quoi l'invité ne verrait même pas
  //    le flux du dossier).
  await api(`/dossiers/${dossierId}/participations`, {
    method: 'POST',
    token: ctx.token,
    body: { userId: invitee.id, role, permissions },
  });

  // 2. Une « enveloppe d'invitation » par appareil de l'invité.
  const keyPackages = await api<{ deviceId: string; keyPackage: string }[]>(
    `/key-packages/claim/${invitee.id}`,
    { method: 'POST', token: ctx.token },
  );

  const group = groupOf(state);
  const processed = [...state.processed];

  for (const kp of keyPackages) {
    const { commit, welcome } = group.addMember(fromBase64(kp.keyPackage));
    processed.push(
      ...(await postHandshakes(ctx, dossierId, group, commit, welcome)),
    );
  }

  // L'arrivant est maintenant membre : on peut lui confier le secret commun du
  // dossier — par le groupe, donc chiffré pour lui seul et les autres membres.
  // Sans cette clé, il verrait les documents sans pouvoir les ouvrir.
  //
  // Ce message part APRÈS les commits : il est donc chiffré à l'epoch où
  // l'arrivant est déjà là, et lui est lisible dès qu'il a rejoint.
  if (state.dossierKey) {
    processed.push(
      ...(await postDossierKey(ctx, dossierId, group, state.dossierKey)),
    );
  }

  const next: DossierState = {
    ...state,
    mls: group.toState(),
    processed,
    messages: state.messages,
  };
  const newRevision = await saveDossierState(
    ctx.token,
    ctx.masterKey,
    ctx.deviceId,
    dossierId,
    next,
    group.epoch(),
    revision,
  );
  return { state: next, revision: newRevision, joined: true };
}

// ---------------------------------------------------------------------------
// Documents
// ---------------------------------------------------------------------------

/**
 * Métadonnées d'un document — **chiffrées avec la clé du dossier**, donc
 * lisibles par tous ses membres et par personne d'autre. Le serveur n'y voit
 * qu'un blob.
 *
 * La clé du contenu y est incluse : chaque document a la sienne, tirée au
 * hasard. C'est ce qui permettra, plus tard, de partager UNE pièce sans livrer
 * tout le dossier.
 */
type DocumentMetadata = {
  name: string;
  mime: string;
  /** Clé de chiffrement du contenu, en base64. */
  key: string;
};

const METADATA_AAD = enc.encode('delo/document-metadata/v1');
const CONTENT_AAD = enc.encode('delo/document-content/v1');

export type DocumentEntry = {
  id: string;
  uploaderId: string;
  sizeBytes: number;
  uploadedAt: string;
  /** `null` si on ne dispose pas (encore) de la clé du dossier. */
  meta: DocumentMetadata | null;
};

type RawDocument = {
  id: string;
  uploaderId: string;
  sizeBytes: string;
  encryptedMetadata: string;
  uploadedAt: string;
  uploadUrl?: string;
};

function readMetadata(
  dossierKey: Uint8Array | null,
  encryptedMetadata: string,
): DocumentMetadata | null {
  if (!dossierKey) return null;
  try {
    return JSON.parse(
      dec.decode(decryptBlob(dossierKey, fromBase64(encryptedMetadata), METADATA_AAD)),
    ) as DocumentMetadata;
  } catch {
    // Document déposé avant qu'on ait la clé, ou clé erronée : on l'affiche
    // comme illisible plutôt que de faire échouer la liste entière.
    return null;
  }
}

export async function listDocuments(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
): Promise<DocumentEntry[]> {
  await loadCrypto();
  const { documents } = await api<{ documents: RawDocument[] }>(
    `/dossiers/${dossierId}/documents`,
    { token: ctx.token },
  );
  return documents.map((d) => ({
    id: d.id,
    uploaderId: d.uploaderId,
    sizeBytes: Number(d.sizeBytes),
    uploadedAt: d.uploadedAt,
    meta: readMetadata(state.dossierKey, d.encryptedMetadata),
  }));
}

/**
 * Dépose un document.
 *
 * Le fichier est chiffré **ici**, puis envoyé **directement** au stockage objet
 * par une URL présignée : le ciphertext ne transite jamais par l'API. Le serveur
 * ne voit ni le contenu, ni le nom, ni le type — seulement une taille et un blob.
 */
export async function uploadDocument(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
  file: File,
): Promise<void> {
  await loadCrypto();
  if (!state.dossierKey) {
    throw new Error(
      "Clé du dossier absente : attends d'avoir rejoint le groupe chiffré.",
    );
  }

  // Une clé par document, tirée au hasard.
  const contentKey = crypto.getRandomValues(new Uint8Array(32));
  const clear = new Uint8Array(await file.arrayBuffer());
  const ciphertext = encryptBlob(contentKey, clear, CONTENT_AAD);

  const metadata: DocumentMetadata = {
    name: file.name,
    mime: file.type || 'application/octet-stream',
    key: toBase64(contentKey),
  };
  const encryptedMetadata = toBase64(
    encryptBlob(state.dossierKey, enc.encode(JSON.stringify(metadata)), METADATA_AAD),
  );

  const registered = await api<RawDocument>(`/dossiers/${dossierId}/documents`, {
    method: 'POST',
    token: ctx.token,
    body: { encryptedMetadata, sizeBytes: ciphertext.length },
  });

  if (!registered.uploadUrl) {
    throw new Error("Le stockage objet n'est pas configuré côté serveur.");
  }

  // PUT direct vers le stockage : l'API n'est pas dans la boucle.
  const put = await fetch(registered.uploadUrl, {
    method: 'PUT',
    body: new Blob([ciphertext as BlobPart]),
  });
  if (!put.ok) {
    throw new Error(`Dépôt refusé par le stockage (HTTP ${put.status}).`);
  }
}

/** Télécharge et déchiffre un document, puis le rend au navigateur. */
export async function downloadDocument(
  ctx: Ctx,
  dossierId: string,
  doc: DocumentEntry,
): Promise<void> {
  await loadCrypto();
  if (!doc.meta) {
    throw new Error('Document illisible : clé du dossier absente.');
  }

  const { downloadUrl } = await api<{ downloadUrl: string }>(
    `/dossiers/${dossierId}/documents/${doc.id}/download-url`,
    { token: ctx.token },
  );

  // GET direct depuis le stockage : le serveur ne voit pas passer le blob (il
  // consigne seulement, dans l'audit, qu'il a émis cette URL).
  const res = await fetch(downloadUrl);
  if (!res.ok) throw new Error(`Téléchargement refusé (HTTP ${res.status}).`);

  const clear = decryptBlob(
    fromBase64(doc.meta.key),
    new Uint8Array(await res.arrayBuffer()),
    CONTENT_AAD,
  );

  const blob = new Blob([clear as BlobPart], { type: doc.meta.mime });
  const href = URL.createObjectURL(blob);

  // L'ancre doit être DANS le document, et l'URL du blob ne doit pas être
  // révoquée avant que le navigateur ait commencé à lire : révoquer tout de
  // suite annulerait le téléchargement.
  const a = document.createElement('a');
  a.href = href;
  a.download = doc.meta.name;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(href);
  }, 60_000);
}

// ---------------------------------------------------------------------------
// Calendrier (échéances du dossier)
// ---------------------------------------------------------------------------

/**
 * Le détail d'un événement — **tout est chiffré ici**, y compris son type et sa
 * criticité.
 *
 * Ces deux-là vivaient autrefois en clair dans une colonne du serveur, présentés
 * comme « non sensibles ». Ils ne l'étaient pas : savoir qu'un dossier a une
 * AUDIENCE le 12 septembre, ou un DÉLAI DE PRESCRIPTION critique le 3 mars, livre
 * le calendrier judiciaire du cabinet — le serveur connaît déjà les participants.
 */
export type EventDetails = {
  title: string;
  description?: string;
  location?: string;
  eventType:
    | 'audience'
    | 'rdv_client'
    | 'echeance_procedurale'
    | 'delai_prescription'
    | 'autre';
  isCritical: boolean;
};

const EVENT_AAD = enc.encode('delo/event-details/v1');

export type EventEntry = {
  id: string;
  startsAt: string;
  endsAt: string | null;
  /** `null` si on ne dispose pas (encore) de la clé du dossier. */
  details: EventDetails | null;
};

type RawEvent = {
  id: string;
  startsAt: string;
  endsAt: string | null;
  encryptedDetails: string;
};

function readDetails(
  dossierKey: Uint8Array | null,
  encryptedDetails: string,
): EventDetails | null {
  if (!dossierKey) return null;
  try {
    return JSON.parse(
      dec.decode(decryptBlob(dossierKey, fromBase64(encryptedDetails), EVENT_AAD)),
    ) as EventDetails;
  } catch {
    // Événement posé avant qu'on ait la clé : on l'affiche comme illisible
    // plutôt que de faire échouer le calendrier entier.
    return null;
  }
}

/**
 * Les échéances du dossier, dans l'ordre.
 *
 * Le serveur les trie sur `startsAt` — la date est en clair. C'est la seule chose
 * qu'il voie, et la seule dont il ait besoin : elle lui permettra un jour de
 * rappeler une échéance **sans savoir laquelle**. Pour un avocat, manquer un délai
 * de prescription est une faute disciplinaire ; ce rappel-là vaut la fuite d'une
 * date.
 */
export async function listEvents(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
): Promise<EventEntry[]> {
  await loadCrypto();
  const { events } = await api<{ events: RawEvent[] }>(
    `/dossiers/${dossierId}/events`,
    { token: ctx.token },
  );
  return events.map((e) => ({
    id: e.id,
    startsAt: e.startsAt,
    endsAt: e.endsAt,
    details: readDetails(state.dossierKey, e.encryptedDetails),
  }));
}

/** Pose une échéance. Le détail part chiffré sous la clé du dossier. */
export async function createEvent(
  ctx: Ctx,
  dossierId: string,
  state: DossierState,
  startsAt: Date,
  details: EventDetails,
): Promise<EventEntry> {
  await loadCrypto();
  if (!state.dossierKey) {
    // Sans elle, on écrirait un blob que les autres participants — et nous-mêmes
    // au prochain appareil — ne pourraient pas rouvrir.
    throw new Error(
      'Clé du dossier absente : attends de l’avoir reçue par le groupe.',
    );
  }

  const created = await api<RawEvent>(`/dossiers/${dossierId}/events`, {
    method: 'POST',
    token: ctx.token,
    body: {
      startsAt: startsAt.toISOString(),
      encryptedDetails: toBase64(
        encryptBlob(state.dossierKey, enc.encode(JSON.stringify(details)), EVENT_AAD),
      ),
    },
  });

  return {
    id: created.id,
    startsAt: created.startsAt,
    endsAt: created.endsAt,
    details,
  };
}

export async function deleteEvent(
  ctx: Ctx,
  dossierId: string,
  eventId: string,
): Promise<void> {
  await api(`/dossiers/${dossierId}/events/${eventId}`, {
    method: 'DELETE',
    token: ctx.token,
  });
}

// ---------------------------------------------------------------------------
// Appareils secondaires
// ---------------------------------------------------------------------------

/**
 * Ajoute au groupe les appareils de l'utilisateur qui n'y sont pas encore.
 *
 * Un appareil approuvé ne suffit pas : dans MLS, **chaque appareil est une
 * feuille distincte** de l'arbre. Tant qu'il n'a pas été ajouté au groupe, il ne
 * peut rien déchiffrer — il verrait ses dossiers sans pouvoir les ouvrir.
 *
 * Seul un membre du groupe peut faire cet ajout : c'est donc l'appareil
 * principal qui s'en charge, à la synchronisation. Le nouvel appareil reçoit son
 * Welcome, puis la clé du dossier — exactement comme un nouveau participant.
 */
export async function addMissingOwnDevices(
  ctx: Ctx,
  dossierId: string,
  sync: SyncResult,
): Promise<SyncResult> {
  await loadCrypto();
  if (!sync.joined) return sync;

  const devices = (await listDevices(ctx.token)).filter(
    (d) => d.status === 'active',
  );

  const group = groupOf(sync.state);
  const present = new Set(
    group.members().map((m) => toBase64(m.signatureKey)),
  );
  const missing = devices.filter((d) => !present.has(d.signaturePublicKey));
  if (missing.length === 0) return sync;

  const keyPackages = await api<{ deviceId: string; keyPackage: string }[]>(
    `/key-packages/claim/${ctx.userId}`,
    {
      method: 'POST',
      token: ctx.token,
      // Ciblé : sans ça, on consommerait aussi les KeyPackages des appareils
      // déjà membres, pour rien.
      body: { deviceIds: missing.map((d) => d.id) },
    },
  );

  const processed = [...sync.state.processed];
  for (const kp of keyPackages) {
    const { commit, welcome } = group.addMember(fromBase64(kp.keyPackage));
    processed.push(
      ...(await postHandshakes(ctx, dossierId, group, commit, welcome)),
    );
  }

  if (sync.state.dossierKey) {
    processed.push(
      ...(await postDossierKey(ctx, dossierId, group, sync.state.dossierKey)),
    );
  }

  const next: DossierState = {
    ...sync.state,
    mls: group.toState(),
    processed,
  };
  const revision = await saveDossierState(
    ctx.token,
    ctx.masterKey,
    ctx.deviceId,
    dossierId,
    next,
    group.epoch(),
    sync.revision,
  );
  return { state: next, revision, joined: true };
}

/** Publie le commit (pour les membres présents) puis le Welcome (pour l'arrivant). */
async function postHandshakes(
  ctx: Ctx,
  dossierId: string,
  group: MlsGroup,
  commit: Uint8Array,
  welcome: Uint8Array,
): Promise<string[]> {
  const send = async (tag: number, payload: Uint8Array) => {
    const { id } = await api<{ id: string }>(`/dossiers/${dossierId}/messages`, {
      method: 'POST',
      token: ctx.token,
      body: {
        encryptedPayload: toBase64(tagged(tag, payload)),
        messageKind: 'handshake',
        epochAtSend: group.epoch().toString(),
      },
    });
    return id;
  };
  // Le commit d'abord : les membres présents doivent avancer d'epoch avant de
  // pouvoir traiter la suite du flux.
  return [await send(COMMIT, commit), await send(WELCOME, welcome)];
}

/** Confie le secret commun du dossier à ceux qui viennent de rejoindre. */
async function postDossierKey(
  ctx: Ctx,
  dossierId: string,
  group: MlsGroup,
  dossierKey: Uint8Array,
): Promise<string[]> {
  const { id } = await api<{ id: string }>(`/dossiers/${dossierId}/messages`, {
    method: 'POST',
    token: ctx.token,
    body: {
      encryptedPayload: toBase64(
        group.encryptApplication(
          encodeEnvelope({ k: 'dossier-key', v: toBase64(dossierKey) }),
        ),
      ),
      messageKind: 'application',
      epochAtSend: group.epoch().toString(),
    },
  });
  // Le nôtre : on ne pourra jamais le relire, et on a déjà la clé.
  return [id];
}

/** Membres du groupe MLS (feuilles), pour l'affichage. */
export function groupMembers(state: DossierState): number {
  return groupOf(state).members().length;
}

/** Epoch courant du groupe : avance à chaque changement de composition. */
export function groupEpoch(state: DossierState): bigint {
  return groupOf(state).epoch();
}

export type { CachedMessage };
